import React from 'react'

import { Button, InfoOutlined, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, string } from 'prop-types'

import highlightTranslation from 'Pods/Shared/Utils/highlightTranslation'

import { formatLongDate, formatShortDate } from 'Utils/FormatDate'

import { allowanceShape } from '../../../propTypes'

const USAGE_KEY = 'subscription_management.active.usage'
const EMPHASIS_CLASS = 'cgt-body-base-semibold usage-tracker__emphasis'
const AMOUNT_CLASS = 'cgt-body-md-semibold usage-tracker__emphasis'

function AllowanceUsage({
  allowance,
  isInactive = false,
  isPaused,
  pausedUntil = null,
  pauseStartsAt = null,
  canResume,
  isResumeLoading,
  onResume: handleResume,
  translate,
  locale,
}) {
  const {
    monthlyAllowance, downloadsAvailable, generationWeight, generationsAvailable, resetsAt,
  } = allowance
  const isUsedUp = downloadsAvailable <= 0
  // The BE resets the allowance at the auto-resume, but with a pause scheduled the allowance runs out when it starts.
  const resetRowDate = pauseStartsAt || resetsAt
  const availableForBar = Math.min(Math.max(downloadsAvailable, 0), monthlyAllowance)
  const remainingPercentage = monthlyAllowance > 0 ? (availableForBar / monthlyAllowance) * 100 : 0
  const downloadsLabel = translate(`${USAGE_KEY}.downloads`, { count: downloadsAvailable })
  const generationsLabel = translate(`${USAGE_KEY}.generations`, { count: generationsAvailable })
  const resumeButton = canResume && (
    <Button type="secondary" customClass="usage-tracker__action" onClick={handleResume} disabled={isResumeLoading}>
      {translate('subscription_management.paused.resume_now')}
    </Button>
  )

  if (isPaused) {
    return (
      <>
        <p className="cgt-heading-200 usage-tracker__title">
          {translate('subscription_management.paused.usage_title', {
            date: formatShortDate(pausedUntil, locale, 'UTC'),
          })}
        </p>
        <p className="cgt-body-base usage-tracker__text">
          {translate('subscription_management.paused.usage_description')}
        </p>
        {resumeButton}
      </>
    )
  }

  return (
    <>
      {isUsedUp ? (
        <>
          <p className="cgt-heading-200 usage-tracker__title">{translate(`${USAGE_KEY}.used_up_title`)}</p>
          {resetsAt && (
            <p className="cgt-body-base usage-tracker__text">
              {highlightTranslation(translate, `${USAGE_KEY}.renews_on`, {
                date: formatLongDate(resetsAt, locale, 'UTC'),
              }, EMPHASIS_CLASS)}
            </p>
          )}
        </>
      ) : (
        <>
          <p className="cgt-body-base usage-tracker__text">{translate(`${USAGE_KEY}.left`)}</p>
          <p className="cgt-body-md usage-tracker__amount">
            {highlightTranslation(translate, `${USAGE_KEY}.left_amount`, {
              downloads: downloadsLabel,
              generations: generationsLabel,
            }, AMOUNT_CLASS)}
          </p>
        </>
      )}

      <div
        className="usage-tracker__progress-container usage-tracker__progress-container--restructure"
        role="progressbar"
        aria-valuenow={availableForBar}
        aria-valuemin={0}
        aria-valuemax={monthlyAllowance}
        aria-valuetext={translate(`${USAGE_KEY}.left_amount`, {
          downloads: downloadsLabel,
          generations: generationsLabel,
        })}
        aria-label={translate(`${USAGE_KEY}.left`)}
      >
        <div
          className={classnames(
            'usage-tracker__progress-fill',
            'usage-tracker__progress-fill--restructure',
            isInactive && 'usage-tracker__progress-fill--inactive'
          )}
          style={{ width: `${remainingPercentage}%` }}
        />
      </div>

      {!isUsedUp && resetRowDate && (
        <div className="usage-tracker__reset-row">
          <InfoOutlined width="16" height="16" className="usage-tracker__reset-icon" aria-hidden="true" />
          <span className="cgt-body-base usage-tracker__text">
            {highlightTranslation(translate, `${USAGE_KEY}.${pauseStartsAt ? 'available_until' : 'resets_on'}`, {
              date: formatShortDate(resetRowDate, locale, 'UTC'),
            }, EMPHASIS_CLASS)}
          </span>
        </div>
      )}

      <p className="cgt-caption usage-tracker__note">
        {translate(`${USAGE_KEY}.allowance_note`, {
          downloads: monthlyAllowance,
          generations: Math.floor(monthlyAllowance / generationWeight),
        })}
      </p>

    </>
  )
}

AllowanceUsage.propTypes = {
  allowance: allowanceShape.isRequired,
  isInactive: bool,
  isPaused: bool.isRequired,
  pausedUntil: string,
  pauseStartsAt: string,
  canResume: bool.isRequired,
  isResumeLoading: bool.isRequired,
  onResume: func.isRequired,
  translate: func.isRequired,
  locale: string.isRequired,
}

export default AllowanceUsage
